import { Component, OnDestroy, OnInit, OnChanges, SimpleChanges, ViewChild, ViewEncapsulation, HostListener } from '@angular/core';
import { Router, ActivatedRoute, ParamMap } from '@angular/router';
import { DatePipe, formatDate, formatNumber } from '@angular/common';
import { Title } from "@angular/platform-browser";
import { Observable, concat, Subscription, Subject } from 'rxjs';
import { map, switchMap, concatMap, finalize, takeUntil } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { MessageService, FilterMetadata } from 'primeng/api';

import { PipeTrimStr } from '../shared/pipeselecteditems.pipe';
import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'
import { ComponentCanDeactivate } from 'src/app/shared/pending-changes.guard'
import { Client, CustomerCategory } from 'src/app/shared/client';
import { Person, PersonResponcibilityList } from 'src/app/shared/person';
import { ClientApiService } from 'src/app/services/client-api.service';
import { AgentAdminApiService } from 'src/app/services/agent-admin-api.service';
import { AgentTeamMember } from 'src/app/shared/agent-admin';

/** Same 8-4-4-4-12 hex format `routes/AgentAdmin.ts` validates server-side. */
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-edit-person',
  templateUrl: './edit-person.component.html',
  styleUrls: ['./edit-person.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Person (contact) editor.
 *
 * Loading is driven by `personSubj$` — a `Subject<number>` piped through `switchMap` to
 * `getPersonById`. `loadClientContent` pushes `_perId` to the subject for existing persons
 * or creates a blank `Person` object (PERSID: -2) for new ones.
 *
 * `onConfirm` POSTs (PERSID == -2) and uses `finalize` to route to the new ID, or PUTs
 * and snapshots `personCopy` in the `map` callback.
 *
 * Four tab-visibility flags (`tabNamesSelected`, `tabCompanySelected`, `tabPhonesSelected`,
 * `tabAddressSelected`) control accordion panel rendering. `canChangeCompany` returns `true`
 * only when `_perId === 0` (new person), making the company picker editable at creation time.
 */
export class EditPersonComponent implements OnInit, OnDestroy, ComponentCanDeactivate {

  /*private*/ _perId: number;

/*private _cliId: number;*/

  globalSettinsData: GlobalSettingsData;

  person: Person;
  personCopy: Person;
  personCompany: Client;
  personDepartmen: Client;

  personSubj$: Subject<number> = new Subject<number>();

  personResponsibilityList = PersonResponcibilityList;

  /** `true` only for new persons (`_perId === 0`), allowing the company field to be edited. */
  public get canChangeCompany(): boolean {
    return (!this._perId && (this._perId == 0));
  }

  /** `true` when no person property has changed since the last save. */
  public get personDoesNotChanged(): boolean {
    let equal: boolean = true;
    if (this.person && this.personCopy) {
      for (var props in this.person) {
        //console.log('*** EditProductComponent in noChangesThere: props: ' + props + '/' + this.document[props] + '/' + this.documentCopy[props])
        equal = (this.person[props] == this.personCopy[props]);
        if (!equal) break;
      }
    }
    return equal;
  }

  private _initialQuery: Subscription;
  private _personQuery: Subscription;
  private _personSaveQuery: Subscription;
  private _teamsIdentityQuery: Subscription;
  private _teamsIdentitySaveQuery: Subscription;

  tabNamesSelected: boolean = true;
  tabCompanySelected: boolean = true;
  tabPhonesSelected: boolean = false;
  tabAddressSelected: boolean = false;
  tabSignSelected: boolean = false;

  /** Current signature as a data URL for `<img>` display. Null when no signature. */
  signatureDataUrl: string | null = null;

  /** True while signature upload/delete is in progress. */
  signatureUploading: boolean = false;

  /** This person's `AgentTeamsIdentity` row, or `null` if they don't have one
   *  yet -- the common case, most `Person` rows are never Teams-mapped. Kept
   *  as a full row (not just the GUID) so a save from this screen round-trips
   *  `preferred_company_id`/`is_escalation_contact` unchanged instead of
   *  wiping them -- those two are only otherwise editable from the full
   *  Team Members admin tab (`edit-agent-team-members-tab`). */
  teamsIdentity: AgentTeamMember | null = null;
  teamsIdentityCopy: AgentTeamMember | null = null;
  teamsIdentityLoaded: boolean = false;
  teamsIdentitySaving: boolean = false;
  /** `true` once `loadTeamsIdentity` finds a real row -- decides POST vs PUT
   *  on save without a redundant re-fetch. */
  private teamsIdentityExists: boolean = false;

  /** `true` when the entered Teams GUID is well-formed -- gates the save button. */
  public get teamsIdentityValid(): boolean {
    if (!this.teamsIdentity) { return false; }
    return GUID_RE.test((this.teamsIdentity.teams_user_id || '').trim());
  }

  /** Display/edit proxy for the "active" checkbox. Masks the underlying flag
   *  as unchecked whenever the Teams GUID is missing or malformed, so the
   *  checkbox never shows checked for a row that couldn't actually be saved
   *  active -- matches `[disabled]="!teamsIdentityValid"` on the same control.
   *  Deliberately doesn't overwrite `teamsIdentity.active` while the GUID is
   *  invalid, so fixing a mid-edit typo restores the real value instead of a
   *  previously-active row being silently cleared. */
  public get teamsIdentityActiveDisplay(): boolean {
    return this.teamsIdentityValid && !!this.teamsIdentity?.active;
  }
  public set teamsIdentityActiveDisplay(value: boolean) {
    if (this.teamsIdentity) { this.teamsIdentity.active = value; }
  }

  /** `true` when the Teams GUID/active flag haven't changed since the last
   *  load/save -- feeds into `canDeactivate` alongside `personDoesNotChanged`,
   *  since this field lives on the same page but saves through a separate API. */
  public get teamsIdentityDoesNotChanged(): boolean {
    if (!this.teamsIdentity || !this.teamsIdentityCopy) { return true; }
    return this.teamsIdentity.teams_user_id === this.teamsIdentityCopy.teams_user_id
        && this.teamsIdentity.active === this.teamsIdentityCopy.active;
  }

  /** Teams-identity editing requires `portal-admin` -- the backend
   *  (`routes/AgentAdmin.ts`) gates every `/team-members*` route the same
   *  way, so this just avoids showing a control that would 403. */
  public get canManageTeamsIdentity(): boolean {
    return this.globalsettingsservice.hasSomeRoles('portal-admin');
  }

  /** `true` when this contact belongs to the operator's own company --
   *  `PERSCLIID` matches the session's `mainCompanyId` (the same id sent as
   *  the `Webyto-Main-Company` header on every request). Agent team members
   *  are PROXIS/DISTI staff, never a customer/vendor `Client`'s contact, so
   *  the Teams GUID/active controls only make sense for this subset. */
  public get isLocalCompanyContact(): boolean {
    return !!this.person && this.person.PERSCLIID === this.globalSettinsData?.mainCompanyId;
  }

  private destroy$ = new Subject<void>();

  constructor(
    private cliApi: ClientApiService,
    private agentAdminApi: AgentAdminApiService,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title
  ) { }

  /** Guards navigation; blocks when there are unsaved person or Teams-identity changes. */
  @HostListener('window:beforeunload')
  canDeactivate(): Observable<boolean> | boolean {
    return this.personDoesNotChanged && this.teamsIdentityDoesNotChanged;
  }

  /** Unsubscribes all active queries. */
  ngOnDestroy(): void {
    console.log('*** EditPersonComponent in ngOnDestroy')

    this.destroy$.next();
    this.destroy$.complete();

    if (this._initialQuery) { this._initialQuery.unsubscribe(); }
    if (this._personQuery) { this._personQuery.unsubscribe(); }
    if (this._personSaveQuery) { this._personSaveQuery.unsubscribe(); }
    if (this._teamsIdentityQuery) { this._teamsIdentityQuery.unsubscribe(); }
    if (this._teamsIdentitySaveQuery) { this._teamsIdentitySaveQuery.unsubscribe(); }
  }

  /** Reads global settings, wires `personSubj$`, and subscribes to route params. */
  ngOnInit(): void {
    console.log('*** EditPersonComponent in ngOnInit')
    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();

    this._personQuery =
      this.personSubj$
        .pipe(switchMap((id: number) => { return this.cliApi.getPersonById(id) }))
        .subscribe(pr => {
          this.person = pr[0];
          this.personCopy = { ...pr[0] };
          this.titleService.setTitle((this.person) ? 'Редактирование контакта ' + (this.person.PERSONLNAME ?? '>> Без имени <<') : 'Редактирование пустого контакта');

          this.signatureDataUrl = null;
          if (this.person?.PERSSIGNID) {
            this.cliApi.getImageById(this.person.PERSSIGNID)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: img => { this.signatureDataUrl = 'data:' + img.IMAGENOTE + ';base64,' + img.IMAGEDATA; },
                error: () => { this.signatureDataUrl = null; }
              });
          }

          this.teamsIdentity = null;
          this.teamsIdentityCopy = null;
          this.teamsIdentityLoaded = false;
          if (this.canManageTeamsIdentity && this.isLocalCompanyContact && this.person?.PERSID > 0) {
            this.loadTeamsIdentity(this.person.PERSID);
          }
        });

    this._initialQuery =
      this.route.paramMap
      .subscribe((params: ParamMap) => {
        this._perId = +params.get('id');
        console.log('*** EditPersonComponent in param subscribe: (' + this._perId + ') ' + JSON.stringify(params));

          this.loadClientContent();
        });
  }

  /**
   * Triggers a load by pushing `_perId` to `personSubj$` for existing persons, or
   * creates a blank in-memory Person (PERSID: -2) for new-person mode.
   */
  loadClientContent() {
    console.log('*** EditPersonComponent loadClientContent: ===> Started');
    if (this._perId) {
      console.log('*** EditPersonComponent loadClientContent _perId: ' + this._perId);

      this.personSubj$.next(this._perId);

    } else {
      this.person = {
        PERSID: -2,
        PERSCLIID: null,
        PERSONTITLE: null,
        
        PERSONLNAME: null,
        PERSONFNAME: null,
        PERSONMNAME: null,
        PERSONEMAIL: null,
        PERSONCPHONE: null,
        PERSONIDCODE: null,

        PERSONINDEX: null,
        PERSONCOUNTRY: null,
        PERSONCITY: null,
        PERSONADDR1: null,
        PERSONADDR2: null,

        PERSONDEPT: null,
        PERSONNOTE: null,
        PERSONNOTE1: null,

        PERSONPHONE: null,
        PERSONPHONE1: null,
        PERSONFAX: null,
        PERSONFAX1: null,

        PERSSIGNID: null,
        PERSSESSTIME: null,
        PERSSESSIONID: null,
        PERSRESPONSIBLE: null,
        PERSPASSWORD: null,

      };
      this.personCopy = { ... this.person };
      this.titleService.setTitle('Создание нового контакта');
      
    }
    console.log('*** EditPersonComponent loadClientContent: ===> Ended');
  }

  /** Logs the accordion tab index that was opened (no lazy-load activation needed here). */
  onTabOpen(event) {
    console.log('*** EditPersonComponent in onTabOpen: ' + event.index);

    switch (event.index) {
      case 1:
        break;

      case 2:
        break;

      case 3:
        break;

      case 4:
        break;

    }
  }

  /** Delegates to `onConfirm(1)` to save the person. */
  buttonSaveAllClick(event) {
    console.log('*** EditPersonComponent buttonSaveAllClick: ' + JSON.stringify(event))
    environment.production || console.log('*** EditPersonComponent buttonSaveAllClick: client ' + JSON.stringify(this.person))
    environment.production || console.log('*** EditPersonComponent buttonSaveAllClick: clientCopy ' + JSON.stringify(this.personCopy))
    this.onConfirm(1);
  }

  /**
   * Saves the person. POSTs (PERSID == -2) and uses `finalize` to route to the new ID,
   * or PUTs and snapshots `personCopy` on success.
   */
  onConfirm(e: number) {
    console.log('*** EditPersonComponent onConfirm: ' + e)

    switch (e) {
      default:
        console.log('*** EditPersonComponent onConfirm: (Main data) ');
        environment.production || console.log('*** EditPersonComponent buttonSave: client ' + JSON.stringify(this.person));
        environment.production || console.log('*** EditPersonComponent buttonSave: clientCopy ' + JSON.stringify(this.personCopy));

        if (this.person.PERSID == -2) {
          this._personSaveQuery =
            this.cliApi.postPersonById(this.person).pipe(
              map(data => {
                console.log('*** EditPersonComponent onConfirm: post ' + JSON.stringify(data));
                this._perId = data['PERSID'];
                this.personCopy = { ... this.person }; // Stash all changes to allow smooth route
              }),
              finalize(() => {
                console.log('*** EditPersonComponent onConfirm: post and reroute ');
                this.router.navigate(['/edit-person', this._perId]);
              })
            )
              .subscribe();
        } else {
          this._personSaveQuery =
            this.cliApi.putPersonById(this.person).pipe(
              map(data => {
                this.personCopy = { ... this.person };
                console.log('*** EditPersonComponent onConfirm: saved ' + JSON.stringify(data));
              }),
              finalize(() => console.log('*** EditPersonComponent onConfirm: saved '))

            )
              .subscribe();
        }
        break;
    }
  }

  /** Opens all four accordion tabs simultaneously. */
  openAllTabs(event) {
    this.tabNamesSelected = true;
    this.tabCompanySelected = true;
    this.tabPhonesSelected = true;
    this.tabAddressSelected = true;
  }

  /** Shows the names tab and hides all sibling tabs. */
  openNamesTab(event) {
    this.tabNamesSelected = true;
    this.tabCompanySelected = false;
    this.tabPhonesSelected = false;
    this.tabAddressSelected = false;
  }

  /** Shows the company tab and hides all sibling tabs. */
  openCompanyTab(event) {
    this.tabNamesSelected = false;
    this.tabCompanySelected = true;
    this.tabPhonesSelected = false;
    this.tabAddressSelected = false;
  }

  /** Shows the phones tab and hides all sibling tabs. */
  openPhonesTab(event) {
    this.tabNamesSelected = false;
    this.tabCompanySelected = false;
    this.tabPhonesSelected = true;
    this.tabAddressSelected = false;
  }

  /** Shows the address tab and hides all sibling tabs. */
  openAddressTab(event) {
    this.tabNamesSelected = false;
    this.tabCompanySelected = false;
    this.tabPhonesSelected = false;
    this.tabAddressSelected = true;
  }

  /** Navigates to the person editor in new-person mode (id 0). */
  onCreateNewClient(event) {
    console.log('*** EditPersonComponent onCreateNewClient: ' + JSON.stringify(event));
    this.router.navigate(['/edit-person', 0]);
  }

  /** Applies the selected company ID to `person.PERSCLIID`. */
  onCompanySelected(event) {
    console.log('*** EditPersonComponent onCompanySelected: ' + JSON.stringify(event));

    this.person.PERSCLIID = event;
  }

  /** Applies the selected department ID to `person.PERSONDEPT`. */
  onDepartmentSelected(event) {
    console.log('*** EditPersonComponent onDepartmentSelected: ' + JSON.stringify(event));

    this.person.PERSONDEPT = event;
  }

  onSignFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this._perId || this._perId <= 0) { return; }
    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const [header, base64] = dataUrl.split(',');
      const mimeType = header.match(/:(.*?);/)?.[1] ?? file.type;
      this.signatureUploading = true;
      this.cliApi.uploadPersonSign(this._perId, mimeType, base64)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: result => {
            this.person.PERSSIGNID = result.PERSSIGNID;
            this.signatureDataUrl = dataUrl;
            this.signatureUploading = false;
          },
          error: () => { this.signatureUploading = false; }
        });
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  onSignDelete(): void {
    if (!this._perId || !this.person?.PERSSIGNID) { return; }
    this.signatureUploading = true;
    this.cliApi.deletePersonSign(this._perId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => { this.person.PERSSIGNID = null; this.signatureDataUrl = null; this.signatureUploading = false; },
        error: () => { this.signatureUploading = false; }
      });
  }

  /** Loads this person's `AgentTeamsIdentity` row, or seeds a blank one
   *  (`active: false`) so the GUID input has something to bind to before the
   *  first save. Deliberately does NOT match the backend's own insert
   *  default (`active !== false`, i.e. true-unless-explicit) -- seeding true
   *  here made the checkbox render checked before any valid GUID had been
   *  entered. `teamsIdentityActiveDisplay` masks it as unchecked anyway
   *  while the GUID is invalid, but starting from `false` also means a save
   *  made right after a first valid GUID entry, without the checkbox ever
   *  being touched, sends an explicit `active: false` instead of silently
   *  falling back to the backend's true default. */
  loadTeamsIdentity(personId: number): void {
    this._teamsIdentityQuery =
      this.agentAdminApi.getTeamMember(personId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: row => {
            this.teamsIdentity = row || { person_id: personId, teams_user_id: '', preferred_company_id: null, is_escalation_contact: false, active: false };
            this.teamsIdentityCopy = { ...this.teamsIdentity };
            this.teamsIdentityExists = !!row;
            this.teamsIdentityLoaded = true;
          },
          error: err => {
            console.error('*** EditPersonComponent loadTeamsIdentity: Error: ' + err);
            this.teamsIdentityLoaded = true;
            // Non-fatal: the field just won't show/edit anything this load; retry via a page refresh.
          }
        });
  }

  /** Inserts (first save) or updates this person's Teams mapping, preserving
   *  `preferred_company_id`/`is_escalation_contact` unchanged -- those are
   *  only otherwise edited from the full Team Members admin tab, and a PUT
   *  here always sends the whole row so it must round-trip them. */
  saveTeamsIdentity(): void {
    if (!this.teamsIdentity || !this.teamsIdentityValid) { return; }

    this.teamsIdentitySaving = true;
    const call = this.teamsIdentityExists
      ? this.agentAdminApi.updateTeamMember(this.teamsIdentity)
      : this.agentAdminApi.createTeamMember(this.teamsIdentity);
    this._teamsIdentitySaveQuery =
      call.pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.teamsIdentitySaving = false;
          this.teamsIdentityExists = true;
          this.teamsIdentityCopy = { ...this.teamsIdentity };
          this.messageService.add({ severity: 'success', summary: 'Збережено', detail: 'Teams GUID оновлено' });
        },
        error: err => {
          this.teamsIdentitySaving = false;
          console.error('*** EditPersonComponent saveTeamsIdentity: Error: ' + err);
          this.messageService.add({ severity: 'error', summary: 'Не вдалося зберегти', detail: err?.error?.message || err?.message || 'Unknown error' });
        }
      });
  }
}
